import React, { createContext, useContext, useEffect, useState, ReactNode, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { authManager, DirectusAuthState } from '@/lib/directus';
import { syncDirectusAuth } from '@/store/slices/authSlice';

// Context interface
interface DirectusAuthContextType extends DirectusAuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<boolean>;
  refresh: () => Promise<void>;
  forceTokenRefresh: () => Promise<boolean>;
  getTokenInfo: () => {
    hasToken: boolean;
    isExpired: boolean;
    expiresAt: Date | null;
    timeUntilExpiry: number | null;
    willExpireSoon: boolean;
  };
}

// Create context
const DirectusAuthContext = createContext<DirectusAuthContextType | null>(null);

// Provider props
interface DirectusAuthProviderProps {
  children: ReactNode;
}

// Provider component
export const DirectusAuthProvider: React.FC<DirectusAuthProviderProps> = ({ children }) => {
  const dispatch = useDispatch();
  const [authState, setAuthState] = useState<DirectusAuthState>(authManager.getState());
  useRef<boolean>(false);

  useEffect(() => {
    // Register dispatch function for token expiration handling
    authManager.setStoreDispatch(dispatch);
    
    // Subscribe to auth state changes
    const unsubscribe = authManager.subscribe((newState) => {
      setAuthState(newState);
      // Sync with Redux store
      dispatch(syncDirectusAuth({
        user: newState.user,
        isAuthenticated: newState.isAuthenticated
      }));
    });

    // Skip auto-initialization since AuthTokenValidator will handle validation
    // This prevents race conditions and duplicate token checks
    console.log('🔄 DirectusAuthProvider initialized, waiting for token validation...');

    return unsubscribe;
  }, [dispatch]);

  const contextValue: DirectusAuthContextType = {
    ...authState,
    login: authManager.login.bind(authManager),
    logout: authManager.logout.bind(authManager),
    checkAuth: authManager.checkAuth.bind(authManager),
    refresh: authManager.refresh.bind(authManager),
    forceTokenRefresh: authManager.forceTokenRefresh.bind(authManager),
    getTokenInfo: authManager.getTokenInfo.bind(authManager),
  };

  return (
    <DirectusAuthContext.Provider value={contextValue}>
      {children}
    </DirectusAuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useDirectusAuth = (): DirectusAuthContextType => {
  const context = useContext(DirectusAuthContext);
  
  if (!context) {
    throw new Error('useDirectusAuth must be used within a DirectusAuthProvider');
  }
  
  return context;
};

// Auth guard component
interface AuthGuardProps {
  children: ReactNode;
  fallback?: ReactNode;
}

export const DirectusAuthGuard: React.FC<AuthGuardProps> = ({ 
  children, 
  fallback = <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
      <p className="text-muted-foreground">Checking authentication...</p>
    </div>
  </div>
}) => {
  const { isAuthenticated, isLoading } = useDirectusAuth();

  if (isLoading) {
    return <>{fallback}</>;
  }

  if (!isAuthenticated) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

export default DirectusAuthProvider;